import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { TOM_DO_BANNER, useBannersNaCentral } from '@/lib/banner';
import { enderecoDoAviso } from '@/lib/endereco-do-aviso';
import { formatarDataHora, formatarInteiro } from '@/lib/formato';
import { comCorpo, explicarFalha } from '@/lib/pedido';
import {
  ASSUNTO_DO_AVISO,
  ASSUNTO_SEM_TIPO,
  useAvisos,
  useMarcarLidos,
  type Aviso,
  type Explicacao,
} from '@/modulos/avisos/consultas';
import { textoDoDado, textoDoPeriodo } from '@/modulos/avisos/explicacao';
import { UtilidadeDaResposta } from '@/modulos/tecnico/UtilidadeDaResposta';
import { useSessao } from '@/modulos/sessao/consultas';

import { Botao } from './Botao';
import { EsqueletoDeLista } from './Esqueleto';

/**
 * O SINO (0036; ciclo 0, guia 7.3) — os avisos que a reação aos eventos de
 * domínio produz: consumo fora da faixa, preventiva vencendo, estoque abaixo do
 * mínimo, O.S. aberta, documento validado. Cada pessoa vê os de que tem
 * permissão, e a contagem de não lidos vai ESCRITA ao lado do ícone — nunca só
 * um ponto colorido.
 *
 * O desenho é o da p.33: o sino redondo com a contagem em vermelho — vermelho
 * só para o que pede ação (p.24) —, e o popover de 380 px colado a ele, em que
 * cada aviso leva o bloco do seu assunto (o ícone do vocabulário, p.19, na
 * tinta do tom: vermelho pede ação hoje, âmbar pede atenção).
 *
 * ## O aviso explicado (módulo 13, 0039)
 *
 * Cada aviso abre "Ver a regra": a regra que o acionou, o período que
 * ela olhou e os números — o critério mínimo do §5.1 do guia. Quem pergunta à
 * IA pede, por cima disso, a explicação dela, que só cita esses fatos. Aviso
 * de antes da 0039 diz que não guardou a regra, em vez de inventar uma.
 *
 * ## A central de avisos dos banners (R06, p.67)
 *
 * "Um banner por vez; o mais grave fica. Os outros entram na central de
 * avisos." As condições que não estão no banner (`lib/banner`,
 * `useBannersNaCentral`) aparecem no topo do popover, com a mesma ação do
 * banner, e entram na contagem ao lado do sino enquanto valem.
 */

export function Sino() {
  const base = useId();
  const [aberto, setAberto] = useState(false);
  const [explicando, setExplicando] = useState<string | null>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const sessao = useSessao();
  const perguntaAIa = (sessao.data?.permissoes ?? []).includes('ia.perguntar');
  const avisos = useAvisos();
  const marcar = useMarcarLidos();

  useEffect(() => {
    if (!aberto) return;
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') setAberto(false);
    }
    function aoApontar(e: PointerEvent) {
      if (e.target instanceof Node && !caixa.current?.contains(e.target)) setAberto(false);
    }
    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('pointerdown', aoApontar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('pointerdown', aoApontar);
    };
  }, [aberto]);

  const central = useBannersNaCentral();
  const naoLidos = (avisos.data?.naoLidos ?? 0) + central.length;
  const lista = avisos.data?.avisos ?? [];

  return (
    <div ref={caixa} className="relative">
      <Botao
        nivel="fantasma"
        tamanho="p"
        icone
        title="Avisos"
        className="relative"
        aria-expanded={aberto}
        aria-controls={`${base}-avisos`}
        onClick={() => {
          setAberto((a) => !a);
        }}
      >
        <Bell aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />
        {naoLidos > 0 ? (
          <span
            aria-hidden="true"
            className="txt-legenda absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-pill bg-erro px-1 font-semibold tabular-nums text-acao-rotulo"
          >
            {formatarInteiro(naoLidos)}
          </span>
        ) : null}
        {/* O plural concorda com o numero (p.22), sem "(s)". Flexionado aqui
            mesmo, e nao pelo auxiliar do painel: componente compartilhado nao
            depende de modulo. */}
        <span className="sr-only">
          {naoLidos > 0
            ? `Avisos: ${formatarInteiro(naoLidos)} ${naoLidos === 1 ? 'não lido' : 'não lidos'}`
            : 'Avisos'}
        </span>
      </Botao>

      {aberto ? (
        /* Menu ancorado (R01, p.29): colado ao sino a 8 px, abrindo para a
           esquerda — o sino fica na ponta direita da barra, e para a direita
           nao ha espaco. Abaixo de 768 px os 400 px nao cabem de lado nenhum
           (saiam pela esquerda da tela), e a caixa vira folha de largura total
           logo abaixo da barra. */
        <div
          id={`${base}-avisos`}
          role="dialog"
          aria-label="Avisos"
          className="anima-menu absolute top-full right-0 z-[var(--z-dica)] mt-[var(--flutuante-distancia)] flex max-h-[min(560px,calc(100dvh-var(--barra-superior)-var(--esp-6)))] w-[var(--avisos-largura)] flex-col overflow-hidden rounded-card border border-borda-sutil bg-flutuante shadow-elevado max-[768px]:fixed max-[768px]:inset-x-0 max-[768px]:top-[var(--barra-superior)] max-[768px]:mt-0 max-[768px]:max-h-[calc(100dvh-var(--barra-superior))] max-[768px]:w-auto max-[768px]:rounded-none max-[768px]:border-x-0"
        >
          <div className="flex items-center justify-between gap-2 border-b border-borda-sutil px-4 py-3">
            <span className="txt-secao text-tinta">Avisos</span>
            {(avisos.data?.naoLidos ?? 0) > 0 ? (
              <button
                type="button"
                className="link cursor-pointer disabled:cursor-not-allowed disabled:text-tinta-desabilitada"
                disabled={marcar.isPending}
                onClick={() => {
                  marcar.mutate(undefined);
                }}
              >
                Marcar como lidos
              </button>
            ) : null}
          </div>
          <div className="min-h-0 flex-auto overflow-auto">
            {central.length > 0 ? (
              <ul aria-label="Avisos que valem para a tela" className="flex flex-col border-b border-borda-sutil">
                {central.map((b) => {
                  const Icone = b.icone;
                  const tom = TOM_DO_BANNER[b.gravidade];
                  return (
                    <li key={b.id} data-aviso-da-central={b.gravidade} className="flex gap-3 border-b border-borda-sutil px-4 py-3 last:border-b-0">
                      <span aria-hidden="true" className={`flex size-[var(--indicador-bloco)] shrink-0 items-center justify-center rounded-controle ${tom.bloco}`}>
                        <Icone className={`size-[var(--indicador-icone)] ${tom.icone}`} />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="txt-apoio text-tinta">{b.texto}</span>
                        {b.acao ? (
                          <span className="flex justify-end">
                            <Botao
                              nivel="fantasma"
                              tamanho="p"
                              onClick={() => {
                                setAberto(false);
                                b.acao?.aoAcionar();
                              }}
                            >
                              {b.acao.rotulo}
                            </Botao>
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {/* Carregando (p.32): a lista na forma dos avisos — bloco do assunto e
                duas linhas —, e nao uma frase no lugar dela. */}
            {avisos.isPending ? <EsqueletoDeLista oQue="Carregando os avisos" itens={3} className="px-4" /> : null}
            {avisos.isError ? (
              <p role="alert" className="txt-apoio p-4 text-tinta">{`${explicarFalha(avisos.error).erro}. ${explicarFalha(avisos.error).comoResolver}`}</p>
            ) : null}
            {avisos.data && lista.length === 0 && central.length === 0 ? (
              <p className="txt-apoio p-4 text-tinta-secundaria">
                Nenhum aviso. Eles aparecem quando o consumo sai da faixa, uma preventiva vence, o estoque passa do mínimo,
                uma O.S. abre ou um manual é validado.
              </p>
            ) : null}
            <ul className="flex flex-col">
              {lista.map((a) => {
                const assunto = ASSUNTO_DO_AVISO[a.tipo] ?? ASSUNTO_SEM_TIPO;
                const Icone = assunto.icone;
                return (
                <li key={a.id} className="flex gap-3 border-b border-borda-sutil px-4 py-3 last:border-b-0">
                  <span aria-hidden="true" className={`flex size-[var(--indicador-bloco)] shrink-0 items-center justify-center rounded-controle ${assunto.bloco}`}>
                    <Icone className={`size-[var(--indicador-icone)] ${assunto.cor}`} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="flex items-start justify-between gap-2">
                    <span className={`txt-botao ${a.lido ? 'font-medium text-tinta-secundaria' : 'text-tinta'}`}>{a.titulo}</span>
                    {/* Nao lido (p.33): o ponto laranja, e a palavra para o leitor de tela. */}
                    {a.lido ? null : (
                      <span className="mt-1 size-2 shrink-0 rounded-pill bg-marca">
                        <span className="sr-only">Não lido</span>
                      </span>
                    )}
                  </span>
                  <span className="txt-apoio text-tinta-secundaria">{a.texto}</span>
                  {explicando === a.id ? <PorQue aviso={a} perguntaAIa={perguntaAIa} /> : null}
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    <span className="txt-legenda font-normal tabular-nums text-tinta-apagada">{formatarDataHora(new Date(a.criadoEm))}</span>
                    <Botao
                      nivel="fantasma"
                      tamanho="p"
                      className="ml-auto"
                      aria-expanded={explicando === a.id}
                      onClick={() => {
                        setExplicando((atual) => (atual === a.id ? null : a.id));
                      }}
                    >
                      {explicando === a.id ? 'Fechar a regra' : 'Ver a regra'}
                    </Botao>
                    {/* Verbo + objeto (p.15), no terciario da linha, como o
                        "Ver a regra" ao lado: "Abrir" sozinho, preto e
                        sublinhado, nao dizia para onde ia e nao tinha a cor
                        de link do sistema. */}
                    <Link
                      {...enderecoDoAviso(a.rota)}
                      className="botao botao-fantasma botao-p"
                      onClick={() => {
                        if (!a.lido) marcar.mutate([a.id]);
                        setAberto(false);
                      }}
                    >
                      Ir à tela
                    </Link>
                  </span>
                  </span>
                </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A regra, o período e os números do aviso — e, para quem pergunta à IA, a
 * explicação dela. A explicação é pedida só aqui, por clique: cada pedido tem
 * custo, e ninguém paga por aviso que não abriu.
 */
function PorQue({ aviso, perguntaAIa }: { readonly aviso: Aviso; readonly perguntaAIa: boolean }) {
  const explicar = useMutation({
    mutationFn: () => comCorpo<Explicacao>('POST', `/avisos/${aviso.id}/explicacao`, {}),
    /* A explicação aparece logo abaixo do aviso: ela é o retorno. */
    meta: { silencioso: true },
  });

  if (!aviso.regra) {
    return (
      <p className="txt-apoio bloco-rebaixado p-3 text-tinta-secundaria">
        Este aviso é de antes desta versão e não guardou a regra, o período e os números. Os próximos já vêm com eles.
      </p>
    );
  }

  const periodo = textoDoPeriodo(aviso.periodoDe ?? null, aviso.periodoAte ?? null);
  return (
    <div className="bloco-rebaixado flex flex-col gap-2 p-3">
      <p className="txt-apoio text-tinta">
        <span className="font-semibold text-tinta-secundaria">Regra: </span>
        {aviso.regra}
      </p>
      {periodo ? (
        <p className="txt-apoio text-tinta">
          <span className="font-semibold text-tinta-secundaria">Período: </span>
          {periodo}
        </p>
      ) : null}
      {aviso.dados && aviso.dados.length > 0 ? (
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
          {aviso.dados.map((d) => (
            <div key={d.rotulo} className="contents">
              <dt className="txt-apoio text-tinta-secundaria">{d.rotulo}</dt>
              <dd className="txt-apoio text-right tabular-nums text-tinta">
                {/* A unidade em cinza e sem negrito, separada do número (p.23). */}
                {textoDoDado({ ...d, unidade: '' })}
                {d.unidade && textoDoDado(d) !== 'sem valor' ? <span className="text-tinta-apagada">{` ${d.unidade}`}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {perguntaAIa ? (
        explicar.data ? (
          <div className="flex flex-col gap-2">
            <p role="status" className="txt-apoio text-pretty text-tinta">
              {explicar.data.sabe
                ? explicar.data.resposta
                : `A IA não conseguiu explicar só com estes fatos. ${explicar.data.falta ?? ''}`}
            </p>
            {explicar.data.id ? <UtilidadeDaResposta key={explicar.data.id} perguntaId={explicar.data.id} /> : null}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <div>
              <Botao
                nivel="secundario"
                disabled={explicar.isPending}
                onClick={() => {
                  explicar.mutate();
                }}
              >
                {explicar.isPending ? 'Explicando…' : 'Pedir explicação'}
              </Botao>
            </div>
            {explicar.isError ? (
              <p role="alert" className="txt-apoio text-tinta">
                {`${explicarFalha(explicar.error).erro}. ${explicarFalha(explicar.error).comoResolver}`}
              </p>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
}
